import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    useTheme,
    Alert,
    Button,
} from '@mui/material';
import { DeleteOutline, Save, CloudUpload } from '@mui/icons-material';
import { BasicButton } from '../../../../../components/BasicButton';
import apiClient from '../../../../../services/api/ApiClient';
import { ApiEndpoints } from '../../../../../config/constants';
import { PermissionWrapper } from '../../../../../components/PermissionWrapper';

interface SplashLogoPickerProps {
    currentLogo: string | null;
    fallbackLogo: string | null;
    onLogoChange: (newLogoUrl: string | null) => void;
    onPreviewChange?: (previewUrl: string | null) => void;
}

// Allowed file types
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

export const SplashLogoPicker: React.FC<SplashLogoPickerProps> = ({
    currentLogo,
    fallbackLogo,
    onLogoChange,
    onPreviewChange
}) => {
    const theme = useTheme();
    const [isSaving, setIsSaving] = useState(false);
    const [logoUrl, setLogoUrl] = useState<string | null>(currentLogo);
    const [logoBlob, setLogoBlob] = useState<Blob | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Sync with parent prop
    useEffect(() => {
        setLogoUrl(currentLogo);
    }, [currentLogo]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setError('Please select an image file (PNG, JPG, WEBP)');
            return;
        }

        // Validate file size - 2MB
        const maxSize = 2 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('File size must be less than 2MB');
            return;
        }

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setLogoUrl(previewUrl);
        setLogoBlob(file);
        setHasChanges(true);
        setError(null);
        
        // Notify parent immediately for live preview
        onPreviewChange?.(previewUrl);

        // Reset input value so same file can be selected again
        event.target.value = '';
    };

    const handleSave = async () => {
        if (!logoBlob) {
            setError('No file to upload');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const formData = new FormData();
            const mimeToExt: Record<string, string> = {
                'image/webp': 'webp',
                'image/png': 'png',
                'image/jpeg': 'jpg',
                'image/jpg': 'jpg',
            };
            const extension = mimeToExt[logoBlob.type] || 'png';
            formData.append('file', logoBlob, `splash-logo.${extension}`);

            const response = await apiClient.post<{ success: boolean; url: string }>(
                ApiEndpoints.SplashScreen.UploadLogo,
                formData
            );

            // Use the URL from the response with cache busting
            const uploadedUrl = `${response.data.url}?t=${Date.now()}`;
            setLogoUrl(uploadedUrl);
            setLogoBlob(null);
            onLogoChange(uploadedUrl);

            setSuccess('Splash logo saved successfully!');
            setHasChanges(false);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save splash logo');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete the splash logo? It will reset to use the app logo.')) {
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await apiClient.delete(ApiEndpoints.SplashScreen.DeleteLogo);

            setLogoUrl(null);
            setLogoBlob(null);
            setHasChanges(false);
            onLogoChange(null);
            setSuccess('Splash logo deleted successfully!');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete splash logo');
        } finally {
            setIsSaving(false);
        }
    };

    // Display logo: use uploaded logo, or fallback to app logo
    const displayLogo = logoUrl || fallbackLogo;
    const isUsingFallback = !logoUrl && fallbackLogo;

    return (
        <>
            {/* Alerts */}
            {error && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                {isUsingFallback && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: theme.palette.info.main + '20',
                        color: theme.palette.info.main,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        mr: 'auto'
                    }}>
                        Using App Logo
                    </Box>
                )}
                {logoUrl && !isUsingFallback && (
                    <PermissionWrapper screen='splash-screen' permission='delete'>
                        <BasicButton
                            title='Delete'
                            variant='outlined'
                            onClick={handleDelete}
                            isLoading={isSaving}
                            startIcon={<DeleteOutline fontSize='small' />}
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                px: 2,
                                borderColor: theme.palette.error.main,
                                color: theme.palette.error.main,
                                '&:hover': {
                                    borderColor: theme.palette.error.dark,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(244, 67, 54, 0.08)'
                                        : 'rgba(244, 67, 54, 0.04)'
                                }
                            }}
                        />
                    </PermissionWrapper>
                )}
                {hasChanges && (
                    <PermissionWrapper screen='splash-screen' permission='update'>
                        <BasicButton
                            title='Save'
                            variant='contained'
                            onClick={handleSave}
                            isLoading={isSaving}
                            startIcon={<Save fontSize='small' />}
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                px: 2,
                            }}
                        />
                    </PermissionWrapper>
                )}
            </Box>

            {/* Current Logo Preview */}
            {displayLogo && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Box
                        component='img'
                        src={displayLogo}
                        alt='Splash Logo'
                        sx={{
                            width: 120,
                            height: 120,
                            objectFit: 'contain',
                            borderRadius: 2,
                            border: `2px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.02)',
                            p: 1,
                        }}
                    />
                </Box>
            )}

            {/* Upload Section */}
            <Paper
                elevation={0}
                sx={{
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.02)'
                        : 'rgba(0, 0, 0, 0.01)',
                }}
            >
                <CloudUpload
                    sx={{
                        fontSize: 40,
                        color: theme.palette.text.secondary,
                        mb: 1.5,
                    }}
                />
                <Typography
                    variant='body2'
                    sx={{
                        fontFamily: 'primaryFont',
                        fontWeight: 600,
                        mb: 0.5,
                    }}
                >
                    {logoUrl ? 'Change Splash Logo' : 'Upload Splash Logo'}
                </Typography>
                <Typography
                    variant='caption'
                    sx={{
                        color: theme.palette.text.secondary,
                        fontFamily: 'primaryFont',
                        display: 'block',
                        mb: 2,
                    }}
                >
                    Square image recommended (e.g., 512×512), max 2MB
                </Typography>
                <input
                    accept='image/png,image/jpeg,image/jpg,image/webp'
                    style={{ display: 'none' }}
                    id='splash-logo-upload'
                    type='file'
                    onChange={handleFileSelect}
                />
                <label htmlFor='splash-logo-upload' style={{ cursor: 'pointer' }}>
                    <Button
                        variant='outlined'
                        component='span'
                        startIcon={<CloudUpload />}
                        size='small'
                    >
                        Select Image
                    </Button>
                </label>
            </Paper>
        </>
    );
};
